import { useTranslation } from 'next-i18next';
import type { FC } from 'react';

import {
  GMB_SUPPORT_EMAIL, GMB_SUPPORT_PHONE_NUMBER, PATHS, EXTERNAL_PATHS,
} from 'src/common/constants';
import getClassNames from 'src/common/utils/getClassNames';
import UniversalLink from '../UniversalLink';
import styles from './FooterLinks.module.scss';

type FooterNavSection = {
  heading: {
    url?: string;
    i18nKey: string;
  };
  links: {
    url: string;
    i18nKey: string;
    openInNewTab?: boolean;
  }[];
};

const siteLinks: FooterNavSection[] = [
  {
    heading: { i18nKey: 'about' },
    links: [
      { i18nKey: 'about-getmyboat', url: PATHS.ABOUT },
      { i18nKey: 'careers', url: 'https://getmyboat.breezy.hr/' },
      { i18nKey: 'media-inquiries', url: PATHS.PRESS },
      { i18nKey: 'terms-of-use', url: PATHS.TERMS },
      { i18nKey: 'privacy-policy', url: PATHS.PRIVACY_POLICY },
      { i18nKey: 'cookies-policy', url: PATHS.COOKIES_POLICY },
      { i18nKey: 'accessibility-statement', url: PATHS.ACCESSIBILITY_STATEMENT },
      { i18nKey: 'member-interface-agreement', url: PATHS.MEMBER_INTERFACE },
      { i18nKey: 'sitemap', url: EXTERNAL_PATHS.SITEMAP, openInNewTab: true },
      { i18nKey: 'contact-us', url: PATHS.CONTACT_US },
    ],
  },
  {
    heading: { i18nKey: 'learn-more' },
    links: [
      { i18nKey: 'how-it-works', url: PATHS.HOW_IT_WORKS },
      { i18nKey: 'for-owners', url: PATHS.FOR_OWNERS },
      { i18nKey: 'mobile-apps', url: PATHS.MOBILE_APPS },
      { i18nKey: 'safety', url: PATHS.SAFETY },
    ],
  },
  {
    heading: {
      i18nKey: 'experiences',
      url: PATHS.CATEGORIES,
    },
    links: [
      {
        i18nKey: 'boat-rentals',
        url: `${PATHS.SEARCH}`,
      },
      {
        i18nKey: 'jet-ski-rental',
        url: `${PATHS.SEARCH}jet-ski-rental/`,
      },
      {
        i18nKey: 'fishing-charters',
        url: `${PATHS.SEARCH}fishing-charters/`,
      },
      {
        i18nKey: 'houseboat-rental',
        url: `${PATHS.SEARCH}houseboat-rentals/`,
      },
      {
        i18nKey: 'pontoon-rental',
        url: `${PATHS.SEARCH}pontoon-boat-rentals/`,
      },
      {
        i18nKey: 'yacht-rental',
        url: `${PATHS.SEARCH}yacht-rentals/`,
      },
      {
        i18nKey: 'sailboat-rental',
        url: `${PATHS.SEARCH}sailboat-rental/`,
      },
      {
        i18nKey: 'bachelorette-party-page',
        url: `${PATHS.BACHELORETTE_PARTY_PAGE}`,
      },
      {
        i18nKey: 'party-page',
        url: `${PATHS.PARTY_PAGE}`,
      },
    ],
  },
  {
    heading: {
      i18nKey: 'journal',
      url: '/journal/',
    },
    links: [
      {
        i18nKey: 'company-news',
        url: '/journal/company-news/',
      },
      {
        i18nKey: 'lifestyle',
        url: '/journal/lifestyle/',
      },
      {
        i18nKey: 'experiences-guide',
        url: '/journal/experiences/',
      },
      {
        i18nKey: 'destinations',
        url: '/journal/destinations/',
      },
      {
        i18nKey: 'tips-for-renters-and-owners',
        url: '/journal/how-to/',
      },
    ],
  },
  {
    heading: {
      i18nKey: 'popular-destinations',
      url: '/journal/destinations/getmyboats-guides-to-the-world-of-boating/',
    },
    links: [
      {
        i18nKey: 'miami',
        url: `${PATHS.SEARCH}Miami--FL--United-States/`,
      },
      {
        i18nKey: 'lake-lanier',
        url: `${PATHS.SEARCH}Lake-Lanier--GA--United-States/`,
      },
      {
        i18nKey: 'lake-tahoe',
        url: `${PATHS.SEARCH}Lake-Tahoe--United-States/`,
      },
      {
        i18nKey: 'san-diego',
        url: `${PATHS.SEARCH}San-Diego--CA--United-States/`,
      },
      {
        i18nKey: 'lake-travis',
        url: `${PATHS.SEARCH}Lake-Travis--TX--United-States/`,
      },
      {
        i18nKey: 'newport-beach',
        url: `${PATHS.SEARCH}Newport-Beach--CA--United-States/`,
      },
      {
        i18nKey: 'chicago',
        url: `${PATHS.SEARCH}Chicago--IL--United-States/`,
      },
      {
        i18nKey: 'lake-norman',
        url: `${PATHS.SEARCH}Lake-Norman--NC--United-States/`,
      },
      {
        i18nKey: 'key-largo',
        url: `${PATHS.SEARCH}Key-Largo--FL--United-States/`,
      },
      {
        i18nKey: 'destin',
        url: `${PATHS.SEARCH}Destin--FL--United-States/`,
      },
      {
        i18nKey: 'marathon',
        url: `${PATHS.SEARCH}Marathon--FL--United-States/`,
      },
    ],
  },
  {
    heading: {
      i18nKey: 'live-support',
    },
    links: [
      {
        i18nKey: 'help-and-faqs',
        url: 'https://getmyboat.zendesk.com/hc/en-us',
      },
      {
        i18nKey: '+1 818 927 2148',
        url: `tel:${GMB_SUPPORT_PHONE_NUMBER}`,
      },
      {
        i18nKey: GMB_SUPPORT_EMAIL,
        url: `mailto:${GMB_SUPPORT_EMAIL}`,
      },
    ],
  },
];

type SectionTitleProps = {
  heading: {
    url?: string;
    i18nKey: string;
  };
};

const SectionTitle: FC<SectionTitleProps> = ({ heading }) => {
  const [t] = useTranslation('footer');
  return (
    <h4 className={getClassNames(styles, 'header')}>
      { heading.url ? (
        <UniversalLink
          href={heading.url}
          className={styles.linkHref}
        >
          {t(heading.i18nKey)}
        </UniversalLink>
      ) : t(heading.i18nKey)}
    </h4>
  );
};

const FooterLinks: FC = () => {
  const [t] = useTranslation('footer');
  const sections = siteLinks;

  return (
    <div className={styles.row}>
      {sections.map(({ heading, links }) => (
        <div className={styles.column} key={heading.i18nKey}>
          <SectionTitle heading={heading} />
          <ol className={styles.links}>
            {links.map((link) => (
              <li key={link.i18nKey}>
                <UniversalLink
                  href={link.url || '/'}
                  className={styles.linkHref}
                  openInNewTab={link.openInNewTab}
                >
                  {t(link.i18nKey)}
                </UniversalLink>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
};

export default FooterLinks;
